import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "newui/components/ui/Modal";
import { Button } from "newui/components/Button";
import { HelpIcon, useGuidedTour } from "newui/components/ui/Help";
import {
	useAssignmentApply,
	useAssignmentPreview,
} from "newui/features/RolesPermissions/hooks/useUserAssignments";
import type {
	AssignmentPreviewRequest,
	UserAssignment,
} from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { buildAddAccessHelpSteps } from "guides/features/Auth/addAccessSteps";
import { buildCascadeHelpSteps } from "guides/features/Auth/cascadeHelpSteps";
import { userDisplayName } from "newui/features/Settings/components/RolesPermissions/BulkUserPickerModal";
import { AccessModeSelector, type AccessMode } from "./AccessModeSelector";
import { ScopePicker } from "./ScopePicker";
import { AccessConfirmModal } from "./AccessConfirmModal";
import { useScopeSelection } from "./useScopeSelection";

type FlowStep = "mode" | "picker" | "confirm";

const DEFAULT_APPLY_ERROR = "This access could not be applied. Nothing was changed.";

interface AssignmentApplyFailure {
	user_id?: number;
	scope_type?: string;
	scope_id?: number | null;
	code?: string;
	message?: string;
}

/**
 * P0(b) — the confirm modal was swallowing the server's real reason and showing only the
 * generic `rbac.apply_failed`. A guard rejection inside apply's transaction (a genuine race, or
 * the `to_remove` last-System-Admin guard — CONTRACT §11.3, the part §11.3a's amendment leaves
 * unchanged) 422/403s with a `failures[]` array naming which row failed and why (e.g. "Granter's
 * own ceiling does not cover 'Bid Approver'"), the SAME shape and pattern as §6's bulk-revoke
 * failure (see `bulkRevokeFailureMessages` in `ManageRolesModal.tsx` — deliberately mirrored,
 * not reinvented, so both atomic-failure surfaces in this feature behave identically). Rendered
 * as a persistent inline banner (`AccessConfirmModal`'s `applyErrorMessages`), not a toast: the
 * admin needs it on screen while deciding what to do next.
 */
export const applyFailureMessages = (error: unknown): string[] => {
	const data = (
		error as { response?: { data?: { failures?: unknown; message?: string } } }
	)?.response?.data;

	const failures = Array.isArray(data?.failures) ? (data?.failures as AssignmentApplyFailure[]) : [];
	const messages = failures
		.map((failure) => failure?.message)
		.filter((message): message is string => typeof message === "string" && message.length > 0);

	if (messages.length > 0) {
		return messages;
	}

	if (typeof data?.message === "string" && data.message.length > 0) {
		return [data.message];
	}

	if (error instanceof Error && error.message) {
		return [error.message];
	}

	return [DEFAULT_APPLY_ERROR];
};

export interface AddAccessFlowProps {
	isOpen: boolean;
	/**
	 * 1..N users the flow is adding access for. Sourced by D10's wiring from
	 * either `ManageRolesModal`'s own Add button (single user) or
	 * `BulkUserPickerModal`'s `onNext` (multi-user) — this component itself
	 * stays agnostic to which entry point supplied the set.
	 */
	targetUserIds: number[];
	/**
	 * F5 — the full user records behind `targetUserIds`, when the caller has them (multi-user
	 * mode from `BulkUserPickerModal`'s own `onNext(selectedUserIds, selectedUsers)`; single-user
	 * mode from `ManageRolesModal`'s `user` prop). Used ONLY to name confirm-step headers via
	 * `userDisplayName` — never re-derived, never duplicated into this flow's own state. Omit to
	 * fall back to `AccessConfirmModal`'s `User #<id>` placeholder (e.g. the guided-tour path,
	 * which has no real records to show).
	 */
	targetUsers?: UserAssignment[];
	onCancel: () => void;
	onApplied: () => void;
	/**
	 * PRO-730 — lets the "What do I do?" guided tour drive the mode step to
	 * the confirm step without a real role/scope pick, the same `forcedStep`
	 * convention the multi-user bulk wizard this flow replaces used. Setting
	 * this jumps `step` directly, WITHOUT going through `goToConfirm()` — so
	 * no real `previewAssignment` call fires for whatever dummy/empty target
	 * set the tour is demonstrating with. Omit for normal use.
	 */
	forcedStep?: FlowStep;
}

// D8 — Add flow: mode choice (T1) -> scope picker (T2, specific mode only)
// -> cascade confirmation + preview/apply (T3). Composes T1/T2/T3 and owns
// the step machine plus the FROZEN preview request object between preview
// and apply (CONTRACT §11.4 — apply must re-send the exact object preview
// used, never a rebuild from possibly-changed picker state).
export const AddAccessFlow = ({
	isOpen,
	targetUserIds,
	targetUsers,
	onCancel,
	onApplied,
	forcedStep,
}: AddAccessFlowProps) => {
	const [step, setStep] = useState<FlowStep>("mode");
	// PRO-757 crash fix (root-caused by the coordinator) — this tour's steps
	// (`buildAddAccessHelpSteps`) all target elements that exist ONLY on the
	// mode step (role-select, both mode cards, wizard-next). The previous code
	// rendered `<Help>` (icon + tour) unconditionally, so it also mounted on
	// the picker step, where every one of those targets resolves to null —
	// react-joyride then crashes trying to read `.nodeName` off that null. A
	// second, related failure: if the tour is actively running when the admin
	// advances/goes back between steps via the app's OWN Next/Back buttons
	// (not Joyride's), the step's targets can disappear out from under a
	// still-`run=true` Joyride instance, which is what left the modal in a
	// visually broken state (stale overlay/scroll-lock styling). Using
	// `useGuidedTour` directly (the same pattern `ScopePicker`'s own cascade
	// help already uses) rather than `<Help>` gives access to `modeHelp.stop`,
	// so leaving the mode step can tear the tour down explicitly (see the
	// effect below) instead of relying on the trigger's unmount ordering.
	// `useMemo([])` — steps are static; calling `buildAddAccessHelpSteps()`
	// inline would hand Joyride a brand-new array identity on every one of
	// AddAccessFlow's re-renders (role pick, mode toggle, every keystroke),
	// which is its own source of needless re-measurement independent of the
	// step-change crash this fix targets.
	const modeHelpSteps = useMemo(() => buildAddAccessHelpSteps(), []);
	const modeHelp = useGuidedTour({ steps: modeHelpSteps });
	// Destructured so the effect below can depend on the stable function
	// identity directly (`useGuidedTour`'s `stop` is a `useCallback` with `[]`
	// deps under the hood) — `modeHelp` itself is a fresh object literal every
	// render, which `react-hooks/exhaustive-deps` can't see through via a
	// member-expression dependency.
	const { stop: stopModeHelp } = modeHelp;
	// Author retest — "background stays very dark after finishing joyride".
	// The cascade-help tour used to live inside `ScopePicker` itself
	// (`useGuidedTour` called directly there), which turned out to be the
	// wrong home for the same reason `modeHelp` was moved out of
	// `AccessModeSelector` by the crash fix: this Modal keeps a closing
	// dialog's children mounted, UNCHANGED, through its own exit transition
	// (Chakra/framer-motion freezes the last-rendered subtree rather than
	// re-reading new props from a re-render its own `isOpen` already turned
	// off), so `ScopePicker` conditionally not rendering — or receiving a
	// fresh "you are inactive now" prop — has NO effect on an already-in-
	// flight exit. Moving Next from the picker step straight to confirm hit
	// exactly this: `ScopePicker` (and whatever tour it owned) was left
	// running, invisibly, under the new confirm modal, forever — nothing was
	// ever going to unmount it. `AddAccessFlow` itself sits OUTSIDE that
	// frozen subtree (it is Modal's own caller, never gated by Modal's own
	// `isOpen`), so it owns this tour now too, stopped via the same
	// `step`-driven effect `modeHelp` already proved out below.
	const cascadeHelpSteps = useMemo(() => buildCascadeHelpSteps(), []);
	const cascadeHelp = useGuidedTour({ steps: cascadeHelpSteps });
	const { stop: stopCascadeHelp } = cascadeHelp;
	const [roleId, setRoleId] = useState<number | null>(null);
	// Author item 5.2 — rides alongside roleId so the confirm step can name the role being added
	// without re-looking it up (AccessModeSelector already has it from the same role select).
	const [roleName, setRoleName] = useState<string | null>(null);
	const [mode, setMode] = useState<AccessMode>("global");
	const selection = useScopeSelection();

	// The exact request object sent to preview, re-sent verbatim to apply.
	// Built once when the confirm step opens and never rebuilt afterwards —
	// CONTRACT §11.4.
	const [frozenRequest, setFrozenRequest] = useState<AssignmentPreviewRequest | null>(null);

	const previewMutation = useAssignmentPreview();
	const applyMutation = useAssignmentApply();

	// A fresh open must never carry over the previous target's in-progress
	// picks (mirrors ManageRolesModal's reset-on-subject-change discipline).
	// `resetRef` always holds the LATEST reset closure — the same
	// ref-mirroring pattern `Help.tsx`'s `startRef` uses — so the effect below
	// only has to depend on the `isOpen` transition itself, not every value
	// the reset touches (satisfies exhaustive-deps genuinely, no suppression).
	const resetRef = useRef<() => void>(() => undefined);
	resetRef.current = () => {
		setStep("mode");
		setRoleId(null);
		setRoleName(null);
		setMode("global");
		setFrozenRequest(null);
		selection.reset();
		previewMutation.reset();
		applyMutation.reset();
	};

	useEffect(() => {
		if (isOpen) {
			resetRef.current();
		}
	}, [isOpen]);

	/**
	 * PRO-757 R2 (Phase 5 code review RED, reproduced in a real browser) — a fresh open was
	 * the ONLY reset. Switching role mid-flow kept the PREVIOUS role's selection state, and
	 * because `exclude[]` is driven by that state (§3.2/§3.5 — in-session picks plus the rows
	 * the picker reported as already granted for the previous `user_id`+`role_id`), every
	 * project and RFx under the previous role's already-granted portfolios silently vanished
	 * from the new role's lists. The RFx list rendered "No matches.", which is a lie, and the
	 * only recovery was closing and reopening the modal.
	 *
	 * "Already granted" and "picked so far" are both facts about ONE (user, role) subject, so
	 * the whole selection starts over when the subject's role changes — `selection.reset()`
	 * clears `selectedScopes`, `alreadyGranted` and any pending downgrade in one go.
	 *
	 * Done here at the flow level rather than by keying `useScopeSelection` on `roleId`:
	 * `AddAccessFlow` owns the selection AND the step machine, so this stays the one place
	 * that decides when a session starts over (the `isOpen` reset above is the other half of
	 * the same rule), and it holds for ANY path that changes `roleId`, not just the mode
	 * step's own select. `reset` is a `useCallback([])`, so this depends on a stable identity
	 * and fires only on a genuine role change — never on the initial render, and never on a
	 * re-render that happens to re-run the effect body.
	 */
	const { reset: resetSelection } = selection;
	const previousRoleIdRef = useRef<number | null>(roleId);
	useEffect(() => {
		if (previousRoleIdRef.current === roleId) {
			return;
		}
		previousRoleIdRef.current = roleId;
		resetSelection();
	}, [roleId, resetSelection]);

	// PRO-757 crash fix — explicit teardown of the mode-step tour, not just
	// conditional rendering of its trigger. Fires whenever the flow closes OR
	// the step moves off "mode" (the only step its targets exist on) while the
	// tour might still be running from an earlier "What do I do?" click.
	// `stop()` is idempotent (a no-op when nothing is running), so this is
	// safe to run on every qualifying render rather than only once.
	useEffect(() => {
		if (!isOpen || step !== "mode") {
			stopModeHelp();
		}
	}, [isOpen, step, stopModeHelp]);

	// Author retest — the cascade-help tour's own explicit teardown, mirroring
	// the effect immediately above exactly (fires whenever the flow closes OR
	// the step moves off "picker" — the only step its targets exist on —
	// while the tour might still be running from an earlier "I cannot find
	// something" click). `stop()` is idempotent, safe on every qualifying
	// render.
	useEffect(() => {
		if (!isOpen || step !== "picker") {
			stopCascadeHelp();
		}
	}, [isOpen, step, stopCascadeHelp]);

	const handleCascadeHelpStart = () => {
		void cascadeHelp.start();
	};

	// PRO-730 — negative ids are tour-fixture dummies (see
	// guides/features/Auth/tourFixtures.ts's negative-id convention). Ported
	// here per the D10 coordinator note: the per-user role drawer this flow
	// replaces carried the only test coverage for this guarantee, and both
	// the drawer and that test are retired by D10 while the guided tour now
	// reaches THIS component's real apply mutation instead. Defence-in-depth
	// alongside Joyride's own `spotlightClicks={false}` and the forced-step
	// mechanism above (which never calls `goToConfirm`, so no real preview
	// fires either).
	const isTourDummyTarget = targetUserIds.some((id) => id < 0);

	const buildRequest = (): AssignmentPreviewRequest | null => {
		if (roleId === null) {
			return null;
		}
		if (mode === "global") {
			return { user_ids: targetUserIds, role_id: roleId, mode: "global" };
		}
		if (selection.selectedScopes.length === 0) {
			return null;
		}
		return {
			user_ids: targetUserIds,
			role_id: roleId,
			mode: "specific",
			scopes: selection.selectedScopes.map((option) => ({
				scope_type: option.scope_type,
				// Guarded by the empty-check above: every specific-mode pick carries a
				// concrete scope_id — only the synthetic `global` option's id is null,
				// and that option never enters selectedScopes (§3.1).
				scope_id: option.scope_id as number,
			})),
		};
	};

	const goToConfirm = () => {
		const request = buildRequest();
		if (!request) {
			return;
		}
		setFrozenRequest(request);
		setStep("confirm");
		previewMutation.mutate(request);
	};

	const handleModeNext = () => {
		if (roleId === null) {
			return;
		}
		if (mode === "global") {
			goToConfirm();
			return;
		}
		setStep("picker");
	};

	const handlePickerNext = () => {
		if (selection.selectedScopes.length === 0) {
			return;
		}
		goToConfirm();
	};

	const handleConfirmBack = () => {
		setFrozenRequest(null);
		previewMutation.reset();
		setStep(mode === "global" ? "mode" : "picker");
	};

	const handleApply = () => {
		if (!frozenRequest || isTourDummyTarget) {
			return;
		}
		applyMutation.mutate(frozenRequest, { onSuccess: onApplied });
	};

	// PRO-730 — drives the step machine directly for the guided tour, bypassing
	// goToConfirm/handleModeNext entirely so no real network call ever fires.
	useEffect(() => {
		if (isOpen && forcedStep !== undefined) {
			setStep(forcedStep);
		}
	}, [isOpen, forcedStep]);

	const previewErrorMessage =
		previewMutation.error instanceof Error ? previewMutation.error.message : null;
	// P0(b) — see `applyFailureMessages` above; empty array = no error.
	const applyErrorMessages = applyMutation.error ? applyFailureMessages(applyMutation.error) : [];

	// F5 — multi-user confirm headers were reading "User #1088" because the picker's full
	// records were discarded on the way in. `targetUsers` (when the caller has it) is looked up
	// by id, per CONTRACT, via the barrel's own `userDisplayName` (the one convention the RBAC
	// surface actually has — UserAssignment.name, with an email fallback for an invited, never-
	// completed user) rather than re-derived here. Falls through to AccessConfirmModal's own
	// `User #<id>` placeholder for any id not present in `targetUsers` (e.g. omitted entirely, or
	// the PRO-730 tour path's dummy ids).
	const userLabel =
		targetUsers && targetUsers.length > 0
			? (userId: number) => {
					const user = targetUsers.find((candidate) => candidate.id === userId);
					return user ? userDisplayName(user) : `User #${userId}`;
			  }
			: undefined;

	const isFirstStep = step === "mode";
	// Author retest — "background stays very dark after finishing joyride".
	// Root cause: this used to be `isFirstStep ? A : B`, so the ELSE branch
	// (rendering `<ScopePicker>`) covered BOTH "picker" AND "confirm" — the
	// outer `<Modal isOpen={... && step !== "confirm"}>` closing was assumed
	// to be enough to hide it, but Chakra keeps a closing modal's children
	// mounted through its own exit transition, and closing this Modal is what
	// OPENS `AccessConfirmModal` at the very same moment. `ScopePicker` (and
	// its own live cascade-help Joyride tour, if the admin had it open) never
	// unmounted at all going picker -> confirm — its dark 40%-opacity overlay
	// just kept sitting there, stacked over the new confirm modal, forever
	// (nothing was ever going to unmount it). Gating on the exact step, not a
	// binary isFirstStep, makes "confirm" render neither step's content, so
	// `ScopePicker`'s own layout (search box, results, chips) stops rendering
	// promptly rather than sitting frozen behind the confirm modal for as
	// long as Chakra's own exit transition takes. The tour itself no longer
	// depends on this at all — see `cascadeHelp`'s own explicit-stop effect
	// above, which is what actually closes the reported bug.
	const isPickerStep = step === "picker";

	/**
	 * PRO-757 author item 2 — "fix the buttons in the modal at the bottom so they are always
	 * visible".
	 *
	 * These actions used to be rendered inline as the LAST children of the modal body, which
	 * `Modal.tsx` makes the scroll region (`scrollBehavior="inside"`). On the picker step the
	 * body's content is roughly twice the body's own height (measured in a real browser at
	 * 1440x900: 1484px of content in a 711px viewport), so Back/Cancel/Next scrolled away with
	 * it and sat ~720px below the modal's bottom edge — unreachable until you scrolled, and
	 * indistinguishable from "the lists overflow the modal". Passing them as `customFooter`
	 * instead puts them in a real `<ModalFooter>` OUTSIDE the scroll region (see
	 * `Modal.tsx`'s `renderFooter`), which is what this flow's own siblings —
	 * `AccessConfirmModal` and `ManageRolesModal` — already did; this component was the
	 * outlier.
	 *
	 * Computed per step because the action set differs (mode: Cancel/Next; picker:
	 * Back + Cancel/Next). `null` on the confirm step: the outer Modal is closed then and
	 * `AccessConfirmModal` renders its own footer, exactly as the body gates its content on
	 * the precise step rather than a binary `isFirstStep`.
	 *
	 * The `data-testid`s are unchanged (`wizard-back`/`wizard-cancel`/`wizard-next`) — the
	 * guided tour's own step targets `[data-testid="wizard-next"]` and the browser specs
	 * target all three. The border-top is the deliberate boundary between the scrolling
	 * content and the pinned actions, so a clipped list reads as "there is more above/below"
	 * rather than as content spilling out of the dialog (item 1).
	 */
	const renderStepActions = () => {
		if (isFirstStep) {
			return (
				<div className="flex w-full justify-between gap-2 border-t border-gray-200 pt-3">
					<Button btntype="plain-border" size="sm" onClick={onCancel} data-testid="wizard-cancel">
						Cancel
					</Button>
					<Button
						btntype="primary"
						size="sm"
						onClick={handleModeNext}
						disabled={roleId === null}
						data-testid="wizard-next"
					>
						Next
					</Button>
				</div>
			);
		}

		if (isPickerStep) {
			return (
				<div className="flex w-full justify-between gap-2 border-t border-gray-200 pt-3">
					<Button
						btntype="plain-border"
						size="sm"
						onClick={() => setStep("mode")}
						data-testid="wizard-back"
					>
						Back
					</Button>
					<div className="flex gap-2">
						<Button
							btntype="plain-border"
							size="sm"
							onClick={onCancel}
							data-testid="wizard-cancel"
						>
							Cancel
						</Button>
						<Button
							btntype="primary"
							size="sm"
							onClick={handlePickerNext}
							disabled={selection.selectedScopes.length === 0}
							data-testid="wizard-next"
						>
							Next
						</Button>
					</div>
				</div>
			);
		}

		return null;
	};

	return (
		<>
			<Modal
				isOpen={isOpen && step !== "confirm"}
				onClose={onCancel}
				title="Add access"
				size="xxl"
				modalType="standard"
				iconType="create"
				// Escape rule (2026-09-24 run standard) — Escape does exactly what this dialog's own
				// Close does, never disabled here (nothing mid-flight blocks abandoning the mode/
				// picker steps the way an in-flight delete blocks DeleteConfirmModal's). Was
				// previously the one dialog in this flow's own lane NOT opted in, while
				// AccessConfirmModal/DowngradeConfirmModal already were — Escape dismissed the
				// confirm layer but not this one underneath it, which is worse than doing nothing.
				closeOnEsc
				// Author retest, item 3 — cascadeHelp's own steps target elements inside
				// this Modal's scrollable body (scope-search-input, scope-chip-container);
				// see `ModalProps.neutralizeInternalPositioning`'s doc comment for why a
				// scrolled target's react-joyride spotlight is drawn off-position without
				// this, and why it's opt-in rather than the default for every Modal.
				neutralizeInternalPositioning
				// PRO-757 author item 1 — the body is (and stays) the ONE scroll region for
				// this modal: the picker's three lists are plain lists, so per `fillBody`'s
				// doc comment they must NOT take an inner cap/overflow of their own (PRO-718
				// — a second scroller inside a Modal has its wheel killed by
				// react-remove-scroll once it bottoms out). This only makes that one
				// scroller's scrollbar deliberate and visible at rest instead of leaving it
				// to the platform's overlay behaviour.
				visibleScrollbar
				customFooter={renderStepActions()}
			>
				<div data-testid="add-access-flow">
					{/* PRO-757 crash fix — the trigger only renders on the step its tour's
					    targets actually exist on (isFirstStep/mode). `TourPortal` itself is
					    intentionally NOT gated the same way: it has to stay mounted across a
					    step change so `modeHelp.stop()` (the effect above) can tear it down
					    gracefully via its own run->false transition, rather than being
					    yanked out of the tree mid-run by this conditional. */}
					{isFirstStep && (
						<HelpIcon
							onClick={() => void modeHelp.start()}
							label="What do I do?"
							data-testid="manage-roles-help"
							// Author item 3.1 — same "Need some help " label as the scope picker's own
							// cascade help trigger (item 4.1), via HelpIcon's existing `parent` slot.
							parent={<span className="mr-1 text-sm text-gray-600">Need some help </span>}
						/>
					)}
					{modeHelp.TourPortal}
					{/* Author retest — cascadeHelp's own portal, hoisted here alongside modeHelp's
					    for the same reason (see cascadeHelp's own comment above): it must stay
					    mounted and controllable from a component Modal's own exit animation can
					    never freeze mid-transition, unlike `ScopePicker` itself. */}
					{cascadeHelp.TourPortal}

					{isFirstStep ? (
						<AccessModeSelector
							targetUserIds={targetUserIds}
							roleId={roleId}
							onRoleChange={(nextRoleId, nextRoleName) => {
								setRoleId(nextRoleId);
								setRoleName(nextRoleName);
							}}
							mode={mode}
							onModeChange={setMode}
						/>
					) : (
						isPickerStep && (
							<ScopePicker
								targetUserIds={targetUserIds}
								roleId={roleId}
								selection={selection}
								onCascadeHelpStart={handleCascadeHelpStart}
							/>
						)
					)}
				</div>
			</Modal>

			<AccessConfirmModal
				isOpen={isOpen && step === "confirm"}
				isLoadingPreview={previewMutation.isPending}
				previewError={previewErrorMessage}
				planForUsers={previewMutation.data?.data ?? []}
				// CONTRACT §11.3a — top level on preview only; defaults to `false` (not actionable)
				// while unresolved, so Confirm never opens up a beat early before the field lands.
				actionable={previewMutation.data?.actionable ?? false}
				isApplying={applyMutation.isPending}
				applyErrorMessages={applyErrorMessages}
				roleName={roleName}
				userLabel={userLabel}
				onBack={handleConfirmBack}
				onCancel={onCancel}
				onConfirm={handleApply}
			/>
		</>
	);
};
